import { toError } from '@moment/observability/logger'
import { MoodysRating, SPRating } from '@moment/trading-types/reference-data'
import { Decimal } from 'decimal.js-light'

import { TargetBandType as TargetBandTypeFields } from '@/components/pages/model-builder/helpers'
import { globalLogger } from '@/lib/logger'
import { ValueUnit } from '@/types/enums/value-unit'

export const SpRatings = (() => {
  try {
    return SPRating
      ? Object.values(SPRating).map((rating) => ({
          label: rating,
          value: rating,
        }))
      : []
  } catch (e) {
    globalLogger.warn('Error creating SP Ratings', { error: toError(e) })
    return []
  }
})()

export const MoodysRatings = (() => {
  try {
    return MoodysRating
      ? Object.values(MoodysRating).map((rating) => ({
          label: rating,
          value: rating,
        }))
      : []
  } catch (e) {
    globalLogger.warn("Error creating Moody's Ratings", { error: toError(e) })
    return []
  }
})()

// The purpose of this map is calculating the S&P Rating value
// and their corresponding indexes in one go instead of having
// to iterate over the enum every time with findIndex().
// Reverse because we want to display them in descending order,
// but calculate them in ascending order (so that AAA > AA).
const SpRatingsIndexes = Object.values(SPRating)
  .reverse()
  .map((value, i) => ({ value, index: i }))
  .reduce(
    (acc, curr) => {
      acc[curr.value] = curr.index
      return acc
    },
    {} as Record<SPRating, number>,
  )

// The purpose of this map is calculating the Moody's Rating value
// and their corresponding indexes in one go instead of having
// to iterate over the enum every time with findIndex().
// Reverse because we want to display them in descending order,
// but calculate them in ascending order (so that Aaa > Aa1).
const MoodysRatingsIndexes = Object.values(MoodysRating)
  .reverse()
  .map((value, i) => ({ value, index: i }))
  .reduce(
    (acc, curr) => {
      acc[curr.value] = curr.index
      return acc
    },
    {} as Record<MoodysRating, number>,
  )

export enum TargetBandOperator {
  Greater = 'greater',
  Less = 'less',
  GreaterThanOrEqualTo = 'greaterThanOrEqualTo',
  LessThanOrEqualTo = 'lessThanOrEqualTo',
}

/**
 * Get the appropriate display unit symbol based on the ValueUnit
 */
export function getUnitDisplay(unit: ValueUnit): string {
  switch (unit) {
    case ValueUnit.Percentage:
      return '%'
    case ValueUnit.Dollars:
      return '$'
    case ValueUnit.Years:
      return 'yrs'
    case ValueUnit.Rating:
      return '' // Ratings don't use a unit symbol
    case ValueUnit.Stars:
      return '★'
    default:
      return '%' // Default to percentage
  }
}

/**
 * Evaluates the relationship between two ratings using a specified operator.
 *
 * @param lhs - The left-hand side rating as a string. This represents the first rating to compare.
 * @param operator - The comparison operator, can be either TargetBandOperator.Greater or TargetBandOperator.Less.
 * @param rhs - The right-hand side rating as a string. This represents the second rating to compare.
 * @returns A boolean indicating whether the comparison is true based on the operator.
 *
 * Converting the ratings to their corresponding indexes using the Indexes map for comparison.
 * If the operator is TargetBandOperator.Greater, it checks if the index of lhs is greater than that of rhs
 * (so if lhs comes before rhs). The inverse is true for TargetBandOperator.Less.
 * If either lhs or rhs is not provided, the function defaults to returning true, assuming no comparison is needed.
 * Lastly, if they're both numbers, it compares the numbers directly.
 */
export const evaluate = (
  lhs: string,
  operator: TargetBandOperator,
  rhs: string,
) => {
  if (!lhs || !rhs) return true

  // Try S&P ratings first
  let lhsIndex = SpRatingsIndexes[lhs as SPRating]
  let rhsIndex = SpRatingsIndexes[rhs as SPRating]

  // If not found in S&P, try Moody's ratings
  if (lhsIndex === undefined || rhsIndex === undefined) {
    lhsIndex = MoodysRatingsIndexes[lhs as MoodysRating]
    rhsIndex = MoodysRatingsIndexes[rhs as MoodysRating]
  }

  // If still not found, treat as numeric values
  if (lhsIndex === undefined || rhsIndex === undefined) {
    lhsIndex = Number(lhs)
    rhsIndex = Number(rhs)
  }

  switch (operator) {
    case TargetBandOperator.Greater:
      return lhsIndex > rhsIndex
    case TargetBandOperator.GreaterThanOrEqualTo:
      return lhsIndex >= rhsIndex
    case TargetBandOperator.Less:
      return lhsIndex < rhsIndex
    case TargetBandOperator.LessThanOrEqualTo:
      return lhsIndex <= rhsIndex
  }
}

/**
 * Calculates min and max values from a target value and a buffer percentage.
 *
 * @param targetString - The target value as a string
 * @param bufferString - The buffer percentage as a string
 * @param unit - The value unit to determine bounds (optional)
 * @param minBound - The minimum allowed value (default: 0)
 * @param maxBound - The maximum allowed value (default: 100 for percentage, Infinity for others)
 * @returns An array containing the min and max values as strings
 */
export const getMinMaxFromBuffer = (
  targetString: string,
  bufferString: string,
  unit?: ValueUnit,
  minBound?: number,
  maxBound?: number,
) => {
  const target = new Decimal(Number(targetString))
  const buffer = new Decimal(Number(bufferString))

  // Determine bounds based on unit if not explicitly provided
  const effectiveMinBound = minBound ?? 0
  const effectiveMaxBound =
    maxBound ?? (unit === ValueUnit.Percentage ? 100 : Infinity)

  // Calculate min and max with bounds checking
  const calculatedMin = target.minus(buffer).toNumber()
  const calculatedMax = target.plus(buffer).toNumber()

  const min = Math.max(effectiveMinBound, calculatedMin).toString()
  const max = Math.min(effectiveMaxBound, calculatedMax).toString()

  return [min, max]
}

/**
 * Maps a TargetBandTypeFields value to the appropriate ValueUnit
 * @param field The field type from TargetBandTypeFields
 * @returns The corresponding ValueUnit
 */
export const getValueUnitForField = (field: string): ValueUnit => {
  switch (field) {
    // Percentage fields
    case TargetBandTypeFields.Coupon:
    case TargetBandTypeFields.LiquidityScore:
      return ValueUnit.Percentage

    // Year fields
    case TargetBandTypeFields.Duration:
    case TargetBandTypeFields.NextCallInYears:
    case TargetBandTypeFields.WorkoutInYears:
    case TargetBandTypeFields.Maturity:
    case TargetBandTypeFields.Convexity:
      return ValueUnit.Years

    // Rating fields
    case TargetBandTypeFields.SpRating:
    case TargetBandTypeFields.MoodysRating:
    case TargetBandTypeFields.LowestRating:
      return ValueUnit.Rating

    // Default to percentage if not recognized
    default:
      return ValueUnit.Percentage
  }
}

/**
 * Format a value to the appropriate decimal precision based on its unit
 * @param value The value to format
 * @param unit The unit type (percentage, dollars, years, rating)
 * @returns The formatted value with appropriate decimal precision
 */
export function formatValueByPrecision(
  value: string,
  unit?: ValueUnit,
): string {
  if (!value || value === '') return value

  const num = parseFloat(value)
  // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
  if (isNaN(num)) return value

  // 2 decimals for dollars and years, 3 decimals for percentage
  const decimalPrecision =
    unit === ValueUnit.Dollars || unit === ValueUnit.Years ? 2 : 3

  // Format to the correct precision, then parse to remove trailing zeros
  return parseFloat(num.toFixed(decimalPrecision)).toString()
}
